/**
 * A `<RECORD>.<KEY>` NAME resolved against a registry of records -- the grammar a source text uses to name one member
 * of an imported const (`BINDINGS_KV.CACHE`, `STATION_DECLARED_TAKT_HOURS.<station>`) by its spelling rather than its
 * value. The binding-accessor families (`const/binding-accessor-families.ts`) and the declared-standard sources
 * (`const/standard-declared-bound.ts`) each spelled the split and the two own-key probes for themselves; one reading
 * here, so the prototype guard cannot hold on one reader and lapse on the other.
 */

/**
 * The member `name` spells in `registry`, split at the FIRST dot, or undefined when there is no dot, the head is empty,
 * or either half is not an OWN key -- so `toString.length` never resolves through a prototype.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the one dotted-name reading both registries' resolvers compose.
 */
export function ownDottedMember(registry: Readonly<Record<string, Readonly<Record<string, unknown>>>>, name: string): unknown {
	const dot = name.indexOf('.');
	if (dot <= 0) return undefined;
	const head = name.slice(0, dot);
	if (!Object.hasOwn(registry, head)) return undefined;
	const record = registry[head];
	const key = name.slice(dot + 1);
	return record !== undefined && Object.hasOwn(record, key) ? record[key] : undefined;
}

/**
 * The UNQUALIFIED tail of a dotted name -- everything after its LAST dot, the whole name when it carries none
 * (`ai_telemetry.logs` -> `logs`, `this.state.value` -> `value`, `id` -> `id`). The split the other way from
 * {@link ownDottedMember}: that reads a member by its head, this names the leaf a qualified spelling ends in -- a
 * warehouse column off its table-qualified form, an assignment target's own property name.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the one `name.slice(name.lastIndexOf('.') + 1)` spelling; its
 * readers are `utils/compliance/rule-helpers/` leaves and rule leaves, which share no other home.
 */
export function lastDottedSegment(name: string): string {
	return name.slice(name.lastIndexOf('.') + 1);
}

/**
 * `tail` QUALIFIED by an optional `head` -- `<head>.<tail>`, or the bare tail when there is no head: a service label over
 * its apex (`analytics` + `federa.com`), a record member under its parent path. The join the other way from
 * {@link lastDottedSegment}.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the one optional-head dotted join; the fleet host grammar
 * (`const/brand-hostnames.ts`) and the record comment walk (`const/record-comment-preservation.ts`) read it.
 */
export function dottedUnder(head: string | null, tail: string): string {
	return head === null ? tail : `${head}.${tail}`;
}
